/**
 * Get Different Theme JavaScript
 * RAINS-inspired interactions and functionality
 */

(function() {
  'use strict';

  // ==========================================================================
  // Header Scroll Behavior (Transparent to Solid)
  // ==========================================================================
  const headerScroll = {
    init() {
      this.header = document.querySelector('.site-header');
      if (!this.header) return;

      this.lastScrollY = 0;
      this.ticking = false;
      this.heroHeight = window.innerHeight * 0.3; // Trigger at 30% of viewport

      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      window.addEventListener('resize', () => {
        this.heroHeight = window.innerHeight * 0.3;
      }, { passive: true });
      
      // Initial check
      this.update();
    },

    onScroll() {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
        this.ticking = true;
      }
    },

    update() {
      const scrollY = window.scrollY;
      
      if (scrollY > this.heroHeight) {
        this.header.classList.add('is-scrolled');
      } else {
        this.header.classList.remove('is-scrolled');
      }

      this.lastScrollY = scrollY;
    }
  };

  // ==========================================================================
  // Mobile Menu
  // ==========================================================================
  const mobileMenu = {
    init() {
      this.menuToggle = document.querySelector('[data-menu-toggle]');
      this.menuClose = document.querySelector('[data-menu-close]');
      this.drawer = document.querySelector('[data-mobile-menu]');
      
      if (!this.menuToggle || !this.drawer) return;

      this.bindEvents();
      this.createOverlay();
    },

    createOverlay() {
      this.overlay = document.createElement('div');
      this.overlay.className = 'mobile-menu-overlay';
      document.body.appendChild(this.overlay);
    },

    bindEvents() {
      this.menuToggle.addEventListener('click', () => this.open());
      
      if (this.menuClose) {
        this.menuClose.addEventListener('click', () => this.close());
      }

      document.addEventListener('click', (e) => {
        if (e.target.classList.contains('mobile-menu-overlay')) {
          this.close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    },

    open() {
      this.isOpen = true;
      this.drawer.classList.add('is-open');
      this.overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      this.menuToggle.setAttribute('aria-expanded', 'true');
    },

    close() {
      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      this.overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
      this.menuToggle.setAttribute('aria-expanded', 'false');
    }
  };

  // ==========================================================================
  // Smooth Scroll
  // ==========================================================================
  const smoothScroll = {
    init() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          const href = anchor.getAttribute('href');
          if (href === '#') return;
          
          const target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            target.scrollIntoView({ behavior: 'smooth' });
          }
        });
      });
    }
  };

  // ==========================================================================
  // Intersection Observer for Animations
  // ==========================================================================
  const scrollAnimations = {
    init() {
      if (!('IntersectionObserver' in window)) return;

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, options);

      document.querySelectorAll('.animate-on-scroll').forEach(el => {
        observer.observe(el);
      });
    }
  };

  // ==========================================================================
  // Product Quick Add (AJAX Cart)
  // ==========================================================================
  const ajaxCart = {
    init() {
      document.querySelectorAll('[data-product-form]').forEach(form => {
        form.addEventListener('submit', (e) => this.onSubmit(e, form));
      });
    },

    async onSubmit(e, form) {
      e.preventDefault();
      
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn.textContent;
      
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding...';

      try {
        const formData = new FormData(form);
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          submitBtn.textContent = 'Added!';
          this.updateCartCount();
          
          setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 2000);
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Cart error:', error);
        submitBtn.textContent = 'Error';
        
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      }
    },

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        // Update all cart count displays
        document.querySelectorAll('.header-btn').forEach(btn => {
          if (btn.textContent.includes('Cart')) {
            const span = btn.querySelector('span');
            if (span) {
              span.textContent = `Cart (${cart.item_count})`;
            }
          }
        });
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    }
  };

  // ==========================================================================
  // Lazy Loading Images
  // ==========================================================================
  const lazyLoad = {
    init() {
      if ('loading' in HTMLImageElement.prototype) {
        document.querySelectorAll('img[loading="lazy"]').forEach(img => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
          }
        });
      } else if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
              }
              imageObserver.unobserve(img);
            }
          });
        });

        document.querySelectorAll('img[data-src]').forEach(img => {
          imageObserver.observe(img);
        });
      }
    }
  };

  // ==========================================================================
  // Hero Text Animation
  // ==========================================================================
  const heroAnimation = {
    init() {
      const heroHeadings = document.querySelectorAll('.hero-slide-heading');
      
      heroHeadings.forEach(heading => {
        // Add animation class after a short delay
        setTimeout(() => {
          heading.classList.add('animate-reveal-text');
        }, 300);
      });
    }
  };

  // ==========================================================================
  // Initialize
  // ==========================================================================
  document.addEventListener('DOMContentLoaded', () => {
    headerScroll.init();
    mobileMenu.init();
    smoothScroll.init();
    scrollAnimations.init();
    ajaxCart.init();
    lazyLoad.init();
    heroAnimation.init();
  });

})();
